import * as React from "react";
import { Button, Card } from "antd";
import { CloseOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useTableKindId } from "../../newcomponents/table/data/useTableKindId";
import { useTableItemQueryData } from "../../newcomponents/table/data/useTableItemQueryData";
import { parseLink } from "../../utils/linkParser/linkParser";
import { CardContent } from "../../components/antd/card/CardContent";
import { Table } from "../../newcomponents/table/table";
import { NameDescriptionColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionColumn";
import { TagsColumn } from "../../newcomponents/table/columns/wellKnown/tagsColumn";
import { CreatedColumn } from "../../newcomponents/table/columns/wellKnown/createdColumn";
import { LastModifiedColumn } from "../../newcomponents/table/columns/wellKnown/lastModifiedColumn";
import { TableModal } from "../../components/antd/TableModal";
import { TableHeaderRefreshButton } from "../../newcomponents/table/components/RefreshButton";
import { CustomTableCPLNQuery } from "../../newcomponents/table/components/TableCplnQuery";
import { NameDescriptionNoLinkColumn } from "../../newcomponents/table/columns/wellKnown/nameDescriptionNoLinkColumn";
import { Secret } from "../../schema/types/secret/entity";
import { SecretTypeColumn } from "../../newcomponents/table/columns/secret/typeColumn";
import { CardTitle } from "../../components/antd/card/CardTitle";
import { getSecretTableItem, SecretTableItem } from "../../mst/kinds/secret.helpers";
import { formSetValue, formTriggerValidation, useCpFormValue } from "./testNewForm/utils";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { cloneDeep } from "lodash";
import { store } from "../../store";
import { GvcInput } from "../../schema/types/gvc/input";
import { KindSelectState } from "../../components/antd/kindselect/KindSelect";

interface Props {}
export const PullSecretItems: React.FC<Props> = ({}) => {
  const { id } = useCpFormContext();

  const { kind: addTableKind, id: addTableId } = useTableKindId("secret", "gvc-pullSecret-items-secret");
  const qData = useTableItemQueryData<Secret>(addTableKind, {
    query: {
      spec: {
        match: "any",
        terms: [
          { property: "type", op: "=", value: "docker" },
          { property: "type", op: "=", value: "gcp" },
          { property: "type", op: "=", value: "ecr" },
        ],
      },
    },
  });

  const [selectionsToAdd, setSelectionsToAdd] = React.useState<string[]>([]);
  const [selectionItems, setSelectionItems] = React.useState<SecretTableItem[]>([]);
  const [itemCache, setItemCache] = React.useState<Record<string, SecretTableItem>>({});
  const [isAddingItem, setIsAddingItem] = React.useState<boolean>(false);

  const secrets = useCpFormValue(`pullSecretItems`);
  const secretTableItems: SecretTableItem[] = React.useMemo(() => secrets.map((s) => getSecretTableItem(s)), [secrets]);

  const onRemoveItem = async (index: number) => {
    const form = store.getState().form.instances[id];
    const values: GvcInput = form.values as any;

    const currentSecrets: KindSelectState[] = cloneDeep(values.pullSecretItems);
    currentSecrets.splice(index, 1);
    formSetValue(id, `pullSecretItems`, currentSecrets);

    formTriggerValidation(id);
  };

  function onOkAddModal() {
    const form = store.getState().form.instances[id];
    const values: GvcInput = form.values as any;

    const currentSecrets: KindSelectState[] = cloneDeep(values.pullSecretItems);

    // TODO selectionsToAdd can be renamed to something with link
    for (const selectionToAdd of selectionsToAdd) {
      const currentLinks = currentSecrets.map((u) => u.link);
      const item = selectionItems.find((i) => i.selfLink === selectionToAdd);
      if (item) {
        itemCache[selectionToAdd] = item;
      }

      if (currentLinks.includes(selectionToAdd)) {
        continue;
      }

      const { name } = parseLink(selectionToAdd, { useInputCtx: true });
      currentSecrets.push({
        value: name,
        link: selectionToAdd,
        item: item,
      });
    }
    // TODO was sorting the links but no more because now it is kindselectstate

    // TODO initialize this item cache on load for detail page
    setItemCache(itemCache);

    formSetValue(id, `pullSecretItems`, currentSecrets);

    setSelectionsToAdd([]);
    setSelectionItems([]);
    setIsAddingItem(false);

    formTriggerValidation(id);
  }

  const currentLinks = secrets.map((s) => s.link);
  const addSecretTableItems: SecretTableItem[] = qData.visibleItems
    .map((item) => getSecretTableItem({ item: item }))
    .filter((item) => !currentLinks.includes(item.selfLink));

  return (
    <>
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>Pull Secrets</CardTitle>
          <CardContent>
            {secretTableItems.length > 0 ? (
              <Table<SecretTableItem>
                tableId="gvc-pullSecret-items-secret-editable"
                data={secretTableItems}
                hideSettings
                columns={[
                  NameDescriptionNoLinkColumn({ enableSort: false }),
                  SecretTypeColumn(),
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                  {
                    id: "action",
                    label: "",
                    enableResize: false,
                    size: 120,
                    cell: ({ row }) => {
                      return (
                        <div className="w-full flex justify-end">
                          <Button
                            htmlType={`button`}
                            type={"text"}
                            icon={<CloseOutlined />}
                            danger
                            onClick={() => onRemoveItem(row.index)}
                          />
                        </div>
                      );
                    },
                  },
                ]}
              />
            ) : null}
            <TableModal
              open={isAddingItem}
              onCancel={() => setIsAddingItem(false)}
              onOk={() => {
                onOkAddModal();
              }}
            >
              <Table<SecretTableItem>
                tableId={addTableId}
                title={"Select Secrets to Add"}
                selectMode="multi"
                selectKey="selfLink"
                selections={selectionsToAdd}
                onSelectionsChange={(keys, items) => {
                  setSelectionsToAdd(keys);
                  setSelectionItems(items!);
                }}
                data={addSecretTableItems}
                headerRenderer={() => {
                  return (
                    <>
                      <TableHeaderRefreshButton onClick={qData.fetchItems} />
                      <CustomTableCPLNQuery
                        tableId={addTableId}
                        kind={addTableKind}
                        onQuery={qData.setQuery}
                        isLoading={qData.isLoadingQueried}
                      />
                    </>
                  );
                }}
                enableFilter
                isLoading={qData.isLoading}
                onSortChange={qData.setSort}
                onSearchChange={qData.setSearch}
                isLoadingMore={qData.isLoadingMore}
                hasMore={qData.hasMore}
                onLoadMore={qData.fetchNextPage}
                columns={[
                  NameDescriptionColumn(undefined, { shouldForceTargetBlank: () => true }),
                  SecretTypeColumn(),
                  TagsColumn(),
                  CreatedColumn(),
                  LastModifiedColumn(),
                ]}
              />
            </TableModal>
            <Button
              htmlType={`button`}
              className="mt-4"
              type={"dashed"}
              icon={<PlusCircleOutlined />}
              onClick={() => setIsAddingItem(true)}
            >
              Add Pull Secret
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
